import { ContentfulModule, DisclosureProps, DocumentWithEmbeddedEntries } from 'libs/contentful/props';
import { ModuleTypes, ModuleTypeName } from './modules';
import { SECTION_MODULE_CONFIG, isSectionModule } from './sectionModules';
import { PageNinetailedData } from '@/libs/contentful/queries/page/ninetailed';
import { ExtendedExperienceConfiguration } from '@/libs/contentful/queries/ninetailed';
import { EntryReplacement, InlineVariable, Reference as NinetailedReference } from '@ninetailed/experience.js-shared';

// Module-level store for current page's disclosure numbering
// This is safe in SSR since each request gets its own module instance
let currentPageDisclosureNumberingMap: DisclosureNumberingMap | undefined;

export type DisclosureNumberingMap = Map<string, number>;

// Disclosure with module index for ordering
export type IndexedDisclosureProps = DisclosureProps & { moduleIndex: number };

// Variant disclosure set for client-side swapping
export interface VariantDisclosureSet {
  experienceId: string;
  variantIndex: number;
  disclosures: DisclosureProps[];
}

/**
 * Shared utility function to sort disclosures by symbols first, then module order, then original order
 * This ensures consistent sorting across both server-side and client-side disclosure processing
 */
export function sortDisclosuresByPriority(disclosures: DisclosureProps[]): DisclosureProps[] {
  return [...disclosures]
    .map((disclosure, index) => ({
      disclosure,
      originalIndex: index,
      moduleIndex: (disclosure as IndexedDisclosureProps).moduleIndex ?? Infinity,
    }))
    .sort((a, b) => {
      const aHasSymbol = !!a.disclosure.symbol;
      const bHasSymbol = !!b.disclosure.symbol;

      // If both have symbols or both don't have symbols, sort by module order then original order
      if (aHasSymbol === bHasSymbol) {
        // First sort by module index (page order) - lower index = earlier on page = lower number
        if (a.moduleIndex !== b.moduleIndex) {
          return a.moduleIndex - b.moduleIndex;
        }
        // Then by original index within the module
        return a.originalIndex - b.originalIndex;
      }

      // Put disclosures with symbols first
      return aHasSymbol ? -1 : 1;
    })
    .map(item => item.disclosure);
}

/**
 * Set the current page's disclosure numbering map
 */
export function setCurrentPageDisclosureNumberingMap(map: DisclosureNumberingMap) {
  currentPageDisclosureNumberingMap = map;
}

/**
 * Get the current page's disclosure numbering map
 */
export function getCurrentPageDisclosureNumberingMap(): DisclosureNumberingMap | undefined {
  return currentPageDisclosureNumberingMap;
}

/**
 * Extract all embedded Disclosure entries from any document with inline entries
 */
export function extractDisclosuresFromDocument(document: DocumentWithEmbeddedEntries | undefined): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  if (!document?.links?.entries?.inline) {
    return disclosures;
  }

  const inlineEntries = document.links.entries.inline;

  inlineEntries.forEach(entry => {
    if (entry.__typename === 'Disclosure' && entry.copy) {
      const disclosure = {
        sys: entry.sys,
        symbol: entry.symbol,
        copy: entry.copy,
      };
      disclosures.push(disclosure);
    }
  });

  return disclosures;
}

/**
 * Generic path navigator function
 */
function navigateToPath(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce(
      (current, part) =>
        current && typeof current === 'object' && current !== null && part in current
          ? (current as Record<string, unknown>)[part]
          : undefined,
      obj,
    );
}

/**
 * Extract disclosures from any path/field combination
 */
function extractFromPath(
  module: unknown,
  path: string,
  fields: string[],
  isArray = false,
  nestedPath?: string,
  nestedExtraction = false,
): DisclosureProps[] {
  const target = path ? navigateToPath(module, path) : module;

  if (!target) return [];

  // Handle nested extraction for PageSection modules
  if (nestedExtraction && isArray && Array.isArray(target)) {
    return extractFromNestedModules(target);
  }

  // Handle nested arrays (for complex cases like FAQ)
  if (nestedPath && Array.isArray(target)) {
    return extractFromNestedArrays(target, nestedPath, fields);
  }

  // Handle arrays
  if (isArray && Array.isArray(target)) {
    return extractFromItems(target, fields);
  }

  // Handle direct fields
  if (!isArray && isObjectWithKeys(target)) {
    return extractFromFields(target, fields);
  }

  return [];
}

/**
 * Extract disclosures from nested array structures
 */
function extractFromNestedArrays(target: unknown[], nestedPath: string, fields: string[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  target.forEach(item => {
    const nestedItems = navigateToPath(item, nestedPath);
    if (Array.isArray(nestedItems)) {
      disclosures.push(...extractFromItems(nestedItems, fields));
    }
  });

  return disclosures;
}

/**
 * Extract disclosures from nested modules (like PageSection or SectionHero)
 */
function extractFromNestedModules(nestedModules: unknown[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  nestedModules.forEach(nestedModule => {
    if (nestedModule && typeof nestedModule === 'object' && '__typename' in nestedModule) {
      const nestedDisclosures = extractDisclosuresFromModule(nestedModule as ModuleTypes[ModuleTypeName]);
      disclosures.push(...nestedDisclosures);
    }
  });

  return disclosures;
}

/**
 * Extract disclosures from an array of items
 */
function extractFromItems(items: unknown[], fields: string[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  items.forEach(item => {
    if (isObjectWithKeys(item)) {
      disclosures.push(...extractFromFields(item, fields));
    }
  });

  return disclosures;
}

/**
 * Extract disclosures from a single object's fields
 */
function extractFromFields(target: Record<string, unknown>, fields: string[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  fields.forEach(field => {
    if (field in target && target[field]) {
      const fieldDisclosures = extractDisclosuresFromDocument(target[field] as DocumentWithEmbeddedEntries);
      disclosures.push(...fieldDisclosures);
    }
  });

  return disclosures;
}

/**
 * Type guard to check if target is an object with string keys
 */
const isObjectWithKeys = (obj: unknown): obj is Record<string, unknown> => {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
};

// Common field presets
const FIELD_PRESETS = {
  HEADLINE_COPY: ['headline', 'copy'],
  HEADLINE_ONLY: ['headline'],
  COPY_ONLY: ['copy'],
  CONTENT_ONLY: ['content'],
  CARD_STANDARD: ['headline', 'headlineSmallRte', 'copy'],
  CARD_HEADLINE_COPY: ['cardHeadlineRte', 'copy'],
  ACCORDION_FIELDS: ['accordionLabelRte', 'accordionCopy'],
};

// Simplified configuration using presets and extraction rules
const MODULE_EXTRACTION_RULES: Partial<
  Record<
    ModuleTypeName,
    Array<{
      path?: string;
      fields: string[];
      isArray?: boolean;
      nestedPath?: string;
      nestedExtraction?: boolean;
    }>
  >
> = {
  Accordion: [
    { fields: FIELD_PRESETS.HEADLINE_COPY },
    { path: 'accordionItems.items', fields: FIELD_PRESETS.ACCORDION_FIELDS, isArray: true },
  ],
  AnimatedMarquee: [{ fields: FIELD_PRESETS.COPY_ONLY }],
  BentoBox: [
    { fields: ['eyebrowRte', 'headline', 'copy'] },
    { path: 'largeCard', fields: ['headlineRte', 'copy'] },
    { path: 'cards.items', fields: FIELD_PRESETS.CARD_HEADLINE_COPY, isArray: true },
  ],
  CalloutIcon: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'content.items', fields: FIELD_PRESETS.CARD_STANDARD, isArray: true },
  ],
  CalloutImage: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'content.items', fields: FIELD_PRESETS.CARD_STANDARD, isArray: true },
  ],
  CalloutGrid: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'cards.items', fields: FIELD_PRESETS.CARD_STANDARD, isArray: true },
  ],
  CardsExpandable: [
    { path: 'cardsExpandableItems.items', fields: ['eyebrow', 'cardHeadline', 'cardCopy'], isArray: true },
    { path: 'cardsExpandableItems.items', nestedPath: 'cardListItems.items', fields: ['copy'], isArray: true },
  ],
  CardsGridComplex: [{ path: 'cards.items', fields: ['eyebrowRte', 'headline', 'copy'], isArray: true }],
  CardsGridSimple: [{ path: 'cards.items', fields: FIELD_PRESETS.HEADLINE_COPY, isArray: true }],
  CardsIcon: [
    { path: 'cardIconItems.items', fields: ['textEyebrowRte', 'cardHeadlineRte', 'cardCopy'], isArray: true },
  ],
  CardsImage: [{ path: 'cardImageItems.items', fields: ['cardHeadlineRte', 'cardCopy'], isArray: true }],
  CardsStacked: [{ path: 'cards.items', fields: ['eyebrow', 'headline', 'shortDescription'], isArray: true }],
  CardCarousel: [{ path: 'cards.items', fields: ['eyebrow', 'headline', 'copy'], isArray: true }],
  CardsVerticalScroll: [{ path: 'cards.items', fields: ['headline', 'copy'], isArray: true }],
  Compare: [{ fields: FIELD_PRESETS.CONTENT_ONLY }],
  ContentBlock: [{ fields: ['eyebrow', 'headline', 'copy'] }],
  CopyAddOnWithButton: [{ fields: FIELD_PRESETS.HEADLINE_ONLY }],
  CrossSell: [{ fields: ['title', 'copy'] }],
  DisclosureBanner: [{ fields: FIELD_PRESETS.COPY_ONLY }],
  Faq: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    {
      path: 'toggleItems.items',
      fields: FIELD_PRESETS.ACCORDION_FIELDS,
      isArray: true,
      nestedPath: 'contentCollection.items',
    },
  ],
  Features: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  FullBleedImageHeadline: [{ fields: ['eyebrow', 'headline'] }],
  HeadlineWithEyebrow: [{ fields: FIELD_PRESETS.HEADLINE_ONLY }],
  HeroConsolidated: [
    { fields: ['eyebrowText', 'headline', 'copy', 'disclaimers'] },
    { path: 'disclaimerModal', fields: FIELD_PRESETS.COPY_ONLY },
  ],
  HubSpot: [{ fields: FIELD_PRESETS.HEADLINE_ONLY }],
  ImpactHeadline: [{ fields: ['richTextHeadline', 'copy'] }],
  Launchpad: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  MapLocator: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  MediaCopy: [
    { fields: ['eyebrowText', 'headline', 'copy'] },
    { path: 'sliderEntryCollection.items', fields: ['content'], isArray: true },
  ],
  Note: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  PageSection: [{ path: 'modulesCollection.items', fields: [], isArray: true, nestedExtraction: true }],
  SectionHero: [
    { path: 'hero', fields: ['eyebrowText', 'headline', 'copy', 'disclaimers'] },
    { path: 'hero.disclaimerModal', fields: FIELD_PRESETS.COPY_ONLY },
    { path: 'heroConsolidated', fields: [], isArray: false, nestedExtraction: true },
  ],
  ResourcesItgBranded: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'resources.items', fields: ['resourceLabelRte', 'resourceCopy'], isArray: true },
  ],
  RichTextEditor: [{ fields: FIELD_PRESETS.CONTENT_ONLY }],
  TeaserLinks: [{ fields: FIELD_PRESETS.HEADLINE_ONLY }],
  Testimonial: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'testimonialItems.items', fields: ['copy', 'attributionRte'], isArray: true },
  ],
};

/**
 * Extract all embedded Disclosure entries from a module's rich text fields
 */
function extractDisclosuresFromModule(module: ModuleTypes[ModuleTypeName]): DisclosureProps[] {
  try {
    const rules = MODULE_EXTRACTION_RULES[module.__typename as ModuleTypeName];
    if (!rules) {
      return [];
    }

    const disclosures = rules.flatMap(
      ({ path = '', fields, isArray = false, nestedPath, nestedExtraction = false }) => {
        const pathDisclosures = extractFromPath(module, path, fields, isArray, nestedPath, nestedExtraction);
        return pathDisclosures;
      },
    );

    return disclosures;
  } catch (error) {
    console.error('Error extracting disclosures from module:', module.__typename, error);
    return [];
  }
}

/**
 * Extract all embedded Disclosure entries from all page modules
 */
export function extractPageDisclosures(modules: ModuleTypes[ModuleTypeName][]): DisclosureProps[] {
  const allDisclosures: IndexedDisclosureProps[] = [];

  modules.forEach((module, moduleIndex) => {
    const moduleDisclosures = extractDisclosuresFromModule(module);
    // Add module index to preserve page ordering
    const indexedDisclosures: IndexedDisclosureProps[] = moduleDisclosures.map(disclosure => ({
      ...disclosure,
      moduleIndex,
    }));

    allDisclosures.push(...indexedDisclosures);
  });
  // Deduplicate disclosures by sys.id, keeping the occurrence from the earliest module (lowest moduleIndex)
  const disclosureMap = new Map<string, IndexedDisclosureProps>();

  allDisclosures.forEach(disclosure => {
    const id = disclosure.sys?.id;
    if (!id) return;

    const existing = disclosureMap.get(id);
    if (!existing || disclosure.moduleIndex < existing.moduleIndex) {
      disclosureMap.set(id, disclosure);
    }
  });

  const uniqueDisclosures = Array.from(disclosureMap.values());

  return sortDisclosuresByPriority(uniqueDisclosures);
}

/**
 * Create a mapping of disclosure sys.id to auto-assigned numbers for disclosures without symbols
 */
export function createDisclosureNumberingMap(disclosures: DisclosureProps[]): DisclosureNumberingMap {
  const map = new Map<string, number>();
  let autoNumberCounter = 0;

  // Sort disclosures using shared sorting utility
  const sortedDisclosures = sortDisclosuresByPriority(disclosures);

  sortedDisclosures.forEach(disclosure => {
    // Only assign numbers to disclosures that don't have symbols
    if (!disclosure.symbol && disclosure.sys?.id) {
      autoNumberCounter++;
      map.set(disclosure.sys.id, autoNumberCounter);
    }
  });

  return map;
}

export function extractPageDisclosuresWithVariants(
  modules: ModuleTypes[ModuleTypeName][],
  ninetailedData?: PageNinetailedData,
  selectedExperiences?: Record<string, number>,
): DisclosureProps[] {
  const allDisclosures: IndexedDisclosureProps[] = [];

  modules.forEach((module, moduleIndex) => {
    let moduleDisclosures: DisclosureProps[] = [];

    // Check if this module has Ninetailed experiments and variant data
    if (module.hasExperiments && ninetailedData && selectedExperiences) {
      const moduleNinetailedData = ninetailedData.get(module.sys?.id);

      if (moduleNinetailedData?.mappedExperiences) {
        // Find the active experience for this module
        const activeExperience = moduleNinetailedData.mappedExperiences.find(
          (exp: ExtendedExperienceConfiguration<ContentfulModule & { id: string }>) =>
            selectedExperiences[exp.id] !== undefined,
        );

        if (activeExperience) {
          const variantIndex = selectedExperiences[activeExperience.id];

          const component = activeExperience.components?.find(
            (comp: EntryReplacement<NinetailedReference> | InlineVariable) =>
              'id' in comp.baseline && comp.baseline.id === module.sys?.id,
          );

          if (component && component.variants && component.variants.length > 0) {
            // For control variant (index 0), use baseline data instead of variant data
            if (variantIndex === 0) {
              // Don't process variant data, let it fall through to baseline extraction
            } else {
              // Map variantIndex to variants array (variantIndex 1 = variants[0], variantIndex 2 = variants[1], etc.)
              const actualVariantIndex = variantIndex - 1;

              // Ensure the variant index is within bounds
              if (actualVariantIndex < component.variants.length) {
                const variantData = component.variants[actualVariantIndex] as Record<string, unknown>;

                // Process variant data for non-control variants using config-driven approach
                const moduleTypeName = module.__typename;

                // Check if this is a section module that extracts from a nested module property
                const config = isSectionModule(moduleTypeName) ? SECTION_MODULE_CONFIG[moduleTypeName] : undefined;
                const shouldExtractNestedModule =
                  config?.variantExtractNestedModule && config.variantDataPath && variantData[config.variantDataPath];

                if (shouldExtractNestedModule) {
                  // Extract from nested module at the configured path (e.g., SectionHero -> hero)
                  // The nested module already has its own __typename
                  moduleDisclosures = extractDisclosuresFromModule(
                    variantData[config.variantDataPath!] as ModuleTypes[ModuleTypeName],
                  );
                } else {
                  // For all other cases: pass the entire variant data with correct typename
                  // Extraction rules will handle path navigation for section modules with properties
                  const variantDataWithType = { ...variantData, __typename: moduleTypeName };
                  moduleDisclosures = extractDisclosuresFromModule(variantDataWithType as ModuleTypes[ModuleTypeName]);
                }
              }
            }
          }
        }
      }
    }

    // If no variant data or no experiments, fall back to baseline module data
    if (moduleDisclosures.length === 0) {
      moduleDisclosures = extractDisclosuresFromModule(module);
    }

    // Add module index to preserve page ordering
    const indexedDisclosures: IndexedDisclosureProps[] = moduleDisclosures.map(disclosure => ({
      ...disclosure,
      moduleIndex,
    }));

    allDisclosures.push(...indexedDisclosures);
  });

  // Deduplicate disclosures by sys.id, keeping the occurrence from the earliest module (lowest moduleIndex)
  const disclosureMap = new Map<string, IndexedDisclosureProps>();

  allDisclosures.forEach(disclosure => {
    const id = disclosure.sys?.id;
    if (!id) return;

    const existing = disclosureMap.get(id);
    if (!existing || disclosure.moduleIndex < existing.moduleIndex) {
      disclosureMap.set(id, disclosure);
    }
  });

  const uniqueDisclosures = Array.from(disclosureMap.values());

  return sortDisclosuresByPriority(uniqueDisclosures);
}

/**
 * Extract disclosures for all possible variant combinations
 * Returns a map of experience combinations to their disclosure sets
 */
export function extractAllVariantDisclosures(
  modules: ModuleTypes[ModuleTypeName][],
  ninetailedData?: PageNinetailedData,
): {
  baseline: DisclosureProps[];
  variants: VariantDisclosureSet[];
} {
  const baseline = extractPageDisclosures(modules);
  const variants: VariantDisclosureSet[] = [];

  if (!ninetailedData) {
    return { baseline, variants };
  }

  // Collect all unique experience/variant combinations
  const experiencesMap = new Map<string, { experienceId: string; variantCount: number }>();

  modules.forEach(module => {
    if (module.hasExperiments) {
      const moduleNinetailedData = ninetailedData.get(module.sys?.id);
      if (moduleNinetailedData?.mappedExperiences) {
        moduleNinetailedData.mappedExperiences.forEach(
          (exp: ExtendedExperienceConfiguration<ContentfulModule & { id: string }>) => {
            const component = exp.components?.find(
              (comp: EntryReplacement<NinetailedReference> | InlineVariable) =>
                'id' in comp.baseline && comp.baseline.id === module.sys?.id,
            );
            if (component?.variants?.length) {
              experiencesMap.set(exp.id, {
                experienceId: exp.id,
                variantCount: component.variants.length,
              });
            }
          },
        );
      }
    }
  });

  // For each experience, extract disclosures for each variant
  // Note: Ninetailed uses 0=baseline, 1=first variant, 2=second variant, etc.
  // So if there are 2 variants in Contentful, we need to extract for indices 0, 1, 2
  experiencesMap.forEach(({ experienceId, variantCount }) => {
    // Extract baseline (0) + all variants (1 to variantCount)
    for (let variantIndex = 0; variantIndex <= variantCount; variantIndex++) {
      const selectedExperiences = { [experienceId]: variantIndex };
      const disclosures = extractPageDisclosuresWithVariants(modules, ninetailedData, selectedExperiences);

      variants.push({
        experienceId,
        variantIndex,
        disclosures,
      });
    }
  });

  return { baseline, variants };
}
